import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search, Building2, MoreVertical, Server, Globe } from 'lucide-react';
import CreateTenantModal from './CreateTenantModal';

interface Tenant {
  id: string;
  slug: string;
  name: string;
  rut: string;
  isActive: boolean;
  createdAt: string;
  dbSchema: string;
  hostingType: 'shared' | 'dedicated';
  externalUrl: string | null;
}

export default function TenantsListPage() {
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);

  useEffect(() => {
    fetchTenants();
  }, []);

  const fetchTenants = async () => {
    try {
      const res = await fetch('/api/super-admin/tenants', {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('admin_token')}`,
        },
      });
      const data = await res.json();
      setTenants(data.data || []);
    } catch (error) {
      console.error('Error fetching tenants:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const filteredTenants = tenants.filter(
    (t) =>
      (t.name || '').toLowerCase().includes(search.toLowerCase()) ||
      (t.slug || '').toLowerCase().includes(search.toLowerCase())
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-4 border-[#233554] border-t-[#229E64] rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl font-bold text-lyrax-text">Tenants</h1>
          <p className="text-lyrax-text-secondary">{tenants.length} workspaces en la plataforma</p>
        </div>
        <button
          onClick={() => setIsModalOpen(true)}
          className="btn-primary flex items-center gap-2"
        >
          <Plus className="w-4 h-4" />
          Nuevo Tenant
        </button>
      </div>

      <CreateTenantModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSuccess={fetchTenants}
      />

      <div className="mb-6">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-lyrax-text-secondary" />
          <input
            type="text"
            placeholder="Buscar por nombre o slug..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="input pl-10"
          />
        </div>
      </div>

      <div className="bg-lyrax-surface rounded-xl shadow-sm border border-lyrax-border overflow-hidden">
        <table className="w-full">
          <thead className="bg-lyrax-surface-hover border-b border-lyrax-border">
            <tr>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Tenant
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Slug
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                RUT
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Hosting
              </th>
              <th className="text-left px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Estado
              </th>
              <th className="text-right px-6 py-3 text-xs font-medium text-lyrax-text-secondary uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="divide-y divide-lyrax-border">
            {filteredTenants.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-lyrax-text-secondary">
                  {search ? 'No se encontraron tenants' : 'No hay tenants creados'}
                </td>
              </tr>
            ) : (
              filteredTenants.map((tenant) => (
                <tr key={tenant.id} className="hover:bg-lyrax-surface-hover">
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-lyrax-primary/10 rounded-lg flex items-center justify-center">
                        <Building2 className="w-5 h-5 text-lyrax-primary" />
                      </div>
                      <span className="font-medium text-lyrax-text">{tenant.name}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-lyrax-text-secondary">{tenant.slug}</td>
                  <td className="px-6 py-4 text-lyrax-text-secondary">{tenant.rut}</td>
                  <td className="px-6 py-4">
                    <div className="flex items-center gap-1.5" title={tenant.hostingType === 'dedicated' ? tenant.externalUrl || 'Servidor dedicado' : 'Servidor compartido Lyrax'}>
                      {tenant.hostingType === 'shared' ? (
                        <Server className="w-4 h-4 text-lyrax-primary" />
                      ) : (
                        <Globe className="w-4 h-4 text-purple-500" />
                      )}
                      <span className={`text-sm ${tenant.hostingType === 'shared' ? 'text-lyrax-primary' : 'text-purple-500'}`}>
                        {tenant.hostingType === 'shared' ? 'Compartido' : 'Dedicado'}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        tenant.isActive
                          ? 'bg-lyrax-success/10 text-lyrax-success'
                          : 'bg-lyrax-surface-hover text-lyrax-text-secondary'
                      }`}
                    >
                      {tenant.isActive ? 'Activo' : 'Inactivo'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <Link
                        to={`/tenants/${tenant.id}`}
                        className="p-2 text-lyrax-text-secondary hover:text-lyrax-primary transition-colors"
                        title="Ver detalle"
                      >
                        <MoreVertical className="w-4 h-4" />
                      </Link>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}
